import type {
  DiscoveredUploadFile,
  ParsedUploadPlan,
  UploadBatchManifest,
  UploadCaseManifest,
  UploadPreviewCase,
  UploadSeriesManifest,
  UploadWorkflow
} from "../types/case-upload-types"

function normalizeRelativePath(rawPath: string): string {
  return rawPath.replace(/\\/g, "/").replace(/^\/+/, "")
}

function isNiftiPath(relativePath: string): boolean {
  const lower = relativePath.toLowerCase()
  return lower.endsWith(".nii") || lower.endsWith(".nii.gz")
}

function isJsonPath(relativePath: string): boolean {
  return relativePath.toLowerCase().endsWith(".json")
}

function isOmeTiffPath(relativePath: string): boolean {
  const lower = relativePath.toLowerCase()
  return lower.endsWith(".ome.tif") || lower.endsWith(".ome.tiff") || lower.endsWith(".tif") || lower.endsWith(".tiff")
}

function stripNiftiOrJsonExtension(relativePath: string): string {
  const lower = relativePath.toLowerCase()
  if (lower.endsWith(".nii.gz")) return relativePath.slice(0, -7)
  if (lower.endsWith(".nii")) return relativePath.slice(0, -4)
  if (lower.endsWith(".json")) return relativePath.slice(0, -5)
  return relativePath
}

function stripTiffExtension(fileName: string): string {
  const lower = fileName.toLowerCase()
  if (lower.endsWith(".ome.tiff")) return fileName.slice(0, -9)
  if (lower.endsWith(".ome.tif")) return fileName.slice(0, -8)
  if (lower.endsWith(".tiff")) return fileName.slice(0, -5)
  if (lower.endsWith(".tif")) return fileName.slice(0, -4)
  return fileName
}

function stripZarrExtension(fileName: string): string {
  return fileName.toLowerCase().endsWith(".zarr") ? fileName.slice(0, -5) : fileName
}

function pathBaseName(relativePath: string): string {
  const normalized = normalizeRelativePath(relativePath)
  const parts = normalized.split("/")
  return parts[parts.length - 1] ?? normalized
}

function toNullableString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null
}

function toNullableInt(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.trunc(value)
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function toNullableFloat(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function toMetadataRecord(data: Record<string, unknown>): Record<string, string> {
  const metadata: Record<string, string> = {}
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) continue
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      metadata[key] = String(value)
    }
  }
  return metadata
}

function toPreviewCases(cases: UploadCaseManifest[]): UploadPreviewCase[] {
  return cases
    .map((uploadCase) => ({
      study_identifier: uploadCase.study_identifier,
      study_description: uploadCase.study_description ?? null,
      series: uploadCase.series.map((series) => ({
        series_identifier: series.series_identifier,
        format: series.format,
        fileCount: series.file_refs.length
      }))
    }))
    .sort((a, b) => a.study_identifier.localeCompare(b.study_identifier))
}

async function parseRadiologyPlan(discoveredFiles: DiscoveredUploadFile[]): Promise<ParsedUploadPlan> {
  const errors: string[] = []
  const normalized = discoveredFiles.map((entry) => ({
    file: entry.file,
    relativePath: normalizeRelativePath(entry.relativePath || entry.file.name)
  }))

  const niftiByKey = new Map<string, (typeof normalized)[number]>()
  const jsonByKey = new Map<string, (typeof normalized)[number]>()

  for (const entry of normalized) {
    const lowerPath = entry.relativePath.toLowerCase()
    if (isNiftiPath(lowerPath)) {
      const key = stripNiftiOrJsonExtension(entry.relativePath).toLowerCase()
      if (niftiByKey.has(key)) {
        errors.push(`Duplicate NIfTI file for '${key}'.`)
      } else {
        niftiByKey.set(key, entry)
      }
      continue
    }

    if (isJsonPath(lowerPath)) {
      const key = stripNiftiOrJsonExtension(entry.relativePath).toLowerCase()
      if (jsonByKey.has(key)) {
        errors.push(`Duplicate JSON sidecar file for '${key}'.`)
      } else {
        jsonByKey.set(key, entry)
      }
    }
  }

  if (niftiByKey.size === 0 && jsonByKey.size === 0) {
    return {
      manifest: null,
      files: [],
      previewCases: [],
      errors: ["No NIfTI or JSON sidecar files were found."]
    }
  }

  const allKeys = new Set<string>([...niftiByKey.keys(), ...jsonByKey.keys()])

  const files: File[] = []
  const uploadCases = new Map<string, UploadCaseManifest>()

  for (const key of Array.from(allKeys).sort()) {
    const niftiEntry = niftiByKey.get(key)
    const jsonEntry = jsonByKey.get(key)

    if (!(niftiEntry && jsonEntry)) {
      const missing = !niftiEntry ? "NIfTI" : "JSON"
      errors.push(`Missing ${missing} pair for '${key}'.`)
      continue
    }

    let parsedJson: unknown
    try {
      parsedJson = JSON.parse(await jsonEntry.file.text())
    } catch {
      errors.push(`Invalid JSON sidecar content in '${jsonEntry.relativePath}'.`)
      continue
    }

    if (!parsedJson || typeof parsedJson !== "object" || Array.isArray(parsedJson)) {
      errors.push(`JSON sidecar '${jsonEntry.relativePath}' must contain an object.`)
      continue
    }

    const metadata = parsedJson as Record<string, unknown>
    const studyIdentifier = toNullableString(metadata.study_identifier)
    if (!studyIdentifier) {
      errors.push(`JSON sidecar '${jsonEntry.relativePath}' is missing required field 'study_identifier'.`)
      continue
    }

    const fallbackSeriesIdentifier = stripNiftiOrJsonExtension(pathBaseName(niftiEntry.relativePath))
    const seriesIdentifier =
      toNullableString(metadata.series_identifier) ??
      toNullableString(metadata.image_identifier) ??
      fallbackSeriesIdentifier

    const fileIndex = files.length
    files.push(niftiEntry.file)

    const series: UploadSeriesManifest = {
      format: "nifti",
      series_identifier: seriesIdentifier,
      series_description: toNullableString(metadata.series_description),
      series_number: toNullableInt(metadata.series_number),
      image_type: toNullableString(metadata.image_type),
      image_plane: toNullableString(metadata.image_plane),
      protocol_name: toNullableString(metadata.protocol_name),
      modality: toNullableString(metadata.modality),
      number_of_rows: toNullableInt(metadata.number_of_rows),
      number_of_columns: toNullableInt(metadata.number_of_columns),
      number_of_frames: toNullableInt(metadata.number_of_frames),
      voxel_size_x: toNullableFloat(metadata.voxel_size_x),
      voxel_size_y: toNullableFloat(metadata.voxel_size_y),
      voxel_size_z: toNullableFloat(metadata.voxel_size_z),
      acquisition_time: toNullableString(metadata.acquisition_time),
      acquisition_datetime: toNullableString(metadata.acquisition_datetime),
      manufacturer: toNullableString(metadata.manufacturer),
      manufacturer_model_name: toNullableString(metadata.manufacturer_model_name),
      software_versions: toNullableString(metadata.software_versions),
      b_value: toNullableFloat(metadata.b_value),
      metadata: toMetadataRecord(metadata),
      file_refs: [
        {
          file_index: fileIndex,
          relative_path: pathBaseName(niftiEntry.relativePath)
        }
      ]
    }

    const existingCase = uploadCases.get(studyIdentifier)
    if (existingCase) {
      existingCase.series.push(series)
      if (!existingCase.study_description) {
        existingCase.study_description = toNullableString(metadata.study_description)
      }
      if (!existingCase.study_date) {
        existingCase.study_date = toNullableString(metadata.study_date)
      }
      if (!existingCase.study_time) {
        existingCase.study_time = toNullableString(metadata.study_time)
      }
      if (!existingCase.patient_id) {
        existingCase.patient_id = toNullableString(metadata.patient_id)
      }
      if (!existingCase.patient_name) {
        existingCase.patient_name = toNullableString(metadata.patient_name)
      }
      if (!existingCase.patient_birth_date) {
        existingCase.patient_birth_date = toNullableString(metadata.patient_birth_date)
      }
      if (!existingCase.patient_sex) {
        existingCase.patient_sex = toNullableString(metadata.patient_sex)
      }
      if (!existingCase.patient_age) {
        existingCase.patient_age = toNullableString(metadata.patient_age)
      }
      continue
    }

    uploadCases.set(studyIdentifier, {
      study_identifier: studyIdentifier,
      source: "upload",
      study_description: toNullableString(metadata.study_description),
      study_date: toNullableString(metadata.study_date),
      study_time: toNullableString(metadata.study_time),
      patient_id: toNullableString(metadata.patient_id),
      patient_name: toNullableString(metadata.patient_name),
      patient_birth_date: toNullableString(metadata.patient_birth_date),
      patient_sex: toNullableString(metadata.patient_sex),
      patient_age: toNullableString(metadata.patient_age),
      sort_metric_value: null,
      series: [series]
    })
  }

  const cases = Array.from(uploadCases.values())
  if (cases.length === 0) {
    if (errors.length === 0) {
      errors.push("No valid radiology cases were found in the selected files.")
    }
    return { manifest: null, files: [], previewCases: [], errors }
  }

  const manifest: UploadBatchManifest = {
    version: 1,
    cases
  }

  return {
    manifest: errors.length > 0 ? null : manifest,
    files,
    previewCases: toPreviewCases(cases),
    errors
  }
}

function splitZarrPath(relativePath: string): { rootPath: string; innerPath: string } | null {
  const segments = normalizeRelativePath(relativePath).split("/")
  const zarrIndex = segments.findIndex((segment) => segment.toLowerCase().endsWith(".zarr"))

  if (zarrIndex < 0) return null

  const rootPath = segments.slice(0, zarrIndex + 1).join("/")
  const innerPath = segments.slice(zarrIndex + 1).join("/")
  if (!(rootPath && innerPath)) return null

  return { rootPath, innerPath }
}

function parsePathologyPlan(discoveredFiles: DiscoveredUploadFile[]): ParsedUploadPlan {
  const errors: string[] = []
  const normalized = discoveredFiles.map((entry) => ({
    file: entry.file,
    relativePath: normalizeRelativePath(entry.relativePath || entry.file.name)
  }))

  const zarrRoots = new Map<string, Array<{ file: File; innerPath: string }>>()
  const tiffFiles: Array<(typeof normalized)[number]> = []

  for (const entry of normalized) {
    const zarrPath = splitZarrPath(entry.relativePath)
    if (zarrPath) {
      const group = zarrRoots.get(zarrPath.rootPath) ?? []
      group.push({ file: entry.file, innerPath: zarrPath.innerPath })
      zarrRoots.set(zarrPath.rootPath, group)
      continue
    }

    if (isOmeTiffPath(entry.relativePath)) {
      tiffFiles.push(entry)
    }
  }

  if (zarrRoots.size === 0 && tiffFiles.length === 0) {
    return {
      manifest: null,
      files: [],
      previewCases: [],
      errors: ["No OME-TIFF files or OME-Zarr directories were found."]
    }
  }

  const files: File[] = []
  const casesByStudyIdentifier = new Map<string, UploadCaseManifest>()

  const registerCase = (studyIdentifier: string, uploadCase: UploadCaseManifest) => {
    if (casesByStudyIdentifier.has(studyIdentifier)) {
      errors.push(`Duplicate pathology case identifier '${studyIdentifier}' in upload batch.`)
      return
    }
    casesByStudyIdentifier.set(studyIdentifier, uploadCase)
  }

  // Group TIFFs by parent folder: files from the same folder become one case
  const tiffsByFolder = new Map<string, Array<(typeof normalized)[number]>>()
  for (const tiffFile of tiffFiles.sort((a, b) => a.relativePath.localeCompare(b.relativePath))) {
    const parts = tiffFile.relativePath.split("/")
    // Files with a parent folder get grouped; bare files (no folder) each become their own case
    const folderKey = parts.length > 1 ? parts.slice(0, -1).join("/") : ""
    if (folderKey) {
      const group = tiffsByFolder.get(folderKey) ?? []
      group.push(tiffFile)
      tiffsByFolder.set(folderKey, group)
    } else {
      // Individual file — one case per TIFF (existing behavior)
      const baseName = pathBaseName(tiffFile.relativePath)
      const studyIdentifier = stripTiffExtension(baseName)
      const fileIndex = files.length
      files.push(tiffFile.file)

      registerCase(studyIdentifier, {
        study_identifier: studyIdentifier,
        source: "upload",
        study_description: baseName,
        series: [
          {
            format: "ome_tiff",
            series_identifier: studyIdentifier,
            series_description: baseName,
            metadata: { source_path: tiffFile.relativePath },
            file_refs: [
              {
                file_index: fileIndex,
                relative_path: baseName
              }
            ]
          }
        ]
      })
    }
  }

  // Folder-grouped TIFFs: each folder becomes one case, each TIFF a series
  for (const [folderPath, groupFiles] of Array.from(tiffsByFolder.entries()).sort(([a], [b]) => a.localeCompare(b))) {
    const folderName = pathBaseName(folderPath)
    const studyIdentifier = folderName
    const series: UploadSeriesManifest[] = groupFiles.map((tiffFile) => {
      const baseName = pathBaseName(tiffFile.relativePath)
      const fileIndex = files.length
      files.push(tiffFile.file)
      return {
        format: "ome_tiff" as const,
        series_identifier: stripTiffExtension(baseName),
        series_description: baseName,
        metadata: { source_path: tiffFile.relativePath },
        file_refs: [
          {
            file_index: fileIndex,
            relative_path: baseName
          }
        ]
      }
    })

    registerCase(studyIdentifier, {
      study_identifier: studyIdentifier,
      source: "upload",
      study_description: folderName,
      series
    })
  }

  for (const [rootPath, entries] of Array.from(zarrRoots.entries()).sort(([a], [b]) => a.localeCompare(b))) {
    const rootDirName = pathBaseName(rootPath)
    const studyIdentifier = stripZarrExtension(rootDirName)
    const seriesFileRefs = entries
      .sort((a, b) => a.innerPath.localeCompare(b.innerPath))
      .map((entry) => {
        const fileIndex = files.length
        files.push(entry.file)
        return {
          file_index: fileIndex,
          relative_path: entry.innerPath
        }
      })

    registerCase(studyIdentifier, {
      study_identifier: studyIdentifier,
      source: "upload",
      study_description: rootDirName,
      series: [
        {
          format: "ome_zarr",
          series_identifier: studyIdentifier,
          series_description: rootDirName,
          root_dir_name: rootDirName,
          metadata: { source_path: rootPath },
          file_refs: seriesFileRefs
        }
      ]
    })
  }

  const cases = Array.from(casesByStudyIdentifier.values())
  if (cases.length === 0) {
    if (errors.length === 0) {
      errors.push("No valid pathology cases were found in the selected files.")
    }
    return { manifest: null, files: [], previewCases: [], errors }
  }

  const manifest: UploadBatchManifest = {
    version: 1,
    cases
  }

  return {
    manifest: errors.length > 0 ? null : manifest,
    files,
    previewCases: toPreviewCases(cases),
    errors
  }
}

export async function buildUploadPlan(
  discoveredFiles: DiscoveredUploadFile[],
  workflow: UploadWorkflow
): Promise<ParsedUploadPlan> {
  if (workflow === "radiology") {
    return parseRadiologyPlan(discoveredFiles)
  }

  return parsePathologyPlan(discoveredFiles)
}
